import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Check, Sparkles, X } from 'lucide-react';

const StepReviewModal = ({ 
  isOpen, 
  onClose, 
  stepId, 
  stepName, 
  onValidated 
}) => {
  const { token } = useAuth();
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (reviewText.trim().length < 10) {
      toast.error('Prends quelques secondes pour partager ton ressenti (min. 10 caractères)');
      return;
    }

    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/journey/save-step-review`, 
        { 
          step_id: stepId,
          review_text: reviewText.trim() 
        },
        { headers }
      );
      
      toast.success('Merci ! Étape validée ✓');
      
      if (onValidated) {
        onValidated(stepId);
      }
      
      // Reset and close
      setReviewText('');
      onClose();
      
    } catch (error) {
      console.error('Error saving review:', error);
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && null} // Prevent closing on backdrop click
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-lg bg-[#0A0A0A] border border-[#D4AF37]/30 p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
              <Check className="text-[#D4AF37]" size={24} />
            </div>
            
            <h2 className="font-serif text-2xl text-white mb-2">
              Étape complétée !
            </h2>
            
            <p className="text-[#E5C585] font-light">
              {stepName}
            </p>
          </div>

          {/* Review Form */}
          <div className="mb-8">
            <label className="block text-white font-medium mb-4">
              Dis-moi sincèrement ce que tu penses de cette étape
            </label>
            
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Ce qui t'a aidée... Ce qui était flou... Ce que tu aurais aimé en plus..."
              className="w-full h-32 bg-[#050505] border border-[#27272A] text-white p-4 font-light placeholder:text-[#52525B] focus:border-[#D4AF37]/50 focus:outline-none transition-colors resize-none"
              autoFocus
            />
            
            <div className="flex items-center justify-between mt-2">
              <p className="text-[#52525B] text-xs">
                Minimum 10 caractères
              </p>
              <p className={`text-xs ${reviewText.length >= 10 ? 'text-[#D4AF37]' : 'text-[#52525B]'}`}>
                {reviewText.length} / 10+
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || reviewText.trim().length < 10}
              className="btn-gold w-full py-4 text-base"
              data-testid="validate-step-btn"
            >
              <Sparkles size={18} className="mr-2" />
              {isSubmitting ? 'Un instant...' : "J'intègre cette étape"}
            </Button>
            
            <p className="text-[#52525B] text-xs text-center">
              Cette review est obligatoire pour débloquer la suite de ton parcours
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StepReviewModal;
